import './Start.css';
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from 'react-helmet';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';



Chart.register(annotationPlugin);
const API_BASE_URL = "https://api.nagae.biz/api";

const ResultPage = ({ email, name }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 从 location.state 中获取数据
  const { 
    basicInformation: stateBasicInfo, 
    answeredQuestions: stateAnsweredQuestions, 
    monthlyTasks: stateMonthlyTasks,
    workHours: stateWorkHours,
    salaryPerHour: stateSalaryPerHour
  } = location.state || {};

  // 将 ROI 相关数据转换为数字并计算指标
  const monthlyTasksNum = Number(stateMonthlyTasks) || 0;
  const workHoursNum = Number(stateWorkHours) || 0;
  const salaryPerHourNum = Number(stateSalaryPerHour) || 0;
  const monthlyWorkHours = Math.floor((monthlyTasksNum * workHoursNum) / 60);
  const monthlySalary = salaryPerHourNum * monthlyWorkHours;
  
  // State 管理
  const [basicInformation, setBasicInformation] = useState(stateBasicInfo || null);
  const [answeredQuestions, setAnsweredQuestions] = useState(stateAnsweredQuestions || []);
  const Score1 = Number(location.state?.Score1) || Number(sessionStorage.getItem('Score1')) || 0;
  const Score2 = Number(location.state?.Score2) || Number(sessionStorage.getItem('Score2')) || 0;
  const Score2_display = Number(location.state?.Score2_dispaly) || Number(sessionStorage.getItem('Score2_display')) || 0;
  const tagResult = JSON.parse(sessionStorage.getItem("tags_result") || "{}");
  // 直接计算 score（无需 useState，如不需要后续修改）
  const score = (Score1 || 0) + (Score2 || 0);
  const [evaluationData, setEvaluationData] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  
  const numericScore = parseFloat(score) || 0;
  const evaluationResult = evaluationData.evaluation_result || {};
  const businessName = localStorage.getItem('business_name') || '（未入力）';
  const token = localStorage.getItem("access_token");
  // 定义 ROI 分析中使用的 savingLower 和 savingUpper
  const parsePercentageRange = (str) => {
    if (!str) return { lower: 0, upper: 0 };
    const cleaned = str.replace("%", "");
    const parts = cleaned.split("~");
    if (parts.length < 2) {
      const val = parseFloat(parts[0]) || 0;
      return { lower: val, upper: val };
    }
    return { lower: parseFloat(parts[0]), upper: parseFloat(parts[1]) };
  };
  const { lower: savingLower, upper: savingUpper } = parsePercentageRange(evaluationResult["時間節約割合"] || "0~0%");

  // Chart references，增加 salary 属性
  const chartRefs = useRef({
    structure: null,
    intervention: null,
    efficiency: null,
    salary: null
  });
  
  // 用于判断组件是否挂载，确保异步操作安全
  const isMounted = useRef(true);

  // Authentication check
  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
    }
    return () => {
      isMounted.current = false;
      Object.values(chartRefs.current).forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
    };
  }, [navigate]);

  // 从 sessionStorage 中加载数据（当 state 中没有数据时）
  useEffect(() => {
    const loadSessionData = () => {
      try {
        if (!basicInformation) {
          const storedBasicInfo = sessionStorage.getItem("basicinfo");
          if (storedBasicInfo) {
            setBasicInformation(JSON.parse(storedBasicInfo));
          }
        }
        if (answeredQuestions.length === 0) {
          const storedAnsweredQuestions = sessionStorage.getItem("answeredQuestions");
          if (storedAnsweredQuestions) {
            setAnsweredQuestions(JSON.parse(storedAnsweredQuestions));
          }
        }
      } catch (e) {
        console.error("Error loading data from sessionStorage", e);
        setError("セッションデータの読み込みに失敗しました。もう一度お試しください。");
      }
    };
    loadSessionData();
  }, [basicInformation, answeredQuestions]);


  useEffect(() => {
    const fetchEvaluationData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("access_token");
        console.log(basicInformation);
        const response = await fetch(`${API_BASE_URL}/evaluation`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ score: score ?? 0 }),
        });
        if (!response.ok) {
          throw new Error(`API responded with status: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted.current && data && Object.keys(data).length > 0) {
          setEvaluationData(data);
        } else {
          throw new Error("受信したデータが無効です。");
        }
      } catch (error) {
        console.error("Error fetching evaluation data:", error);
        if (isMounted.current) {
          setError(`評価データの取得に失敗しました: ${error.message}`);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };    
    fetchEvaluationData();
  }, [score]);

  // 绘制图表
  useEffect(() => {
    if (!loading && evaluationData && Object.keys(evaluationData).length > 0) {
      Object.values(chartRefs.current).forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
      drawCharts();
      sendBackupReportIfNeeded();
    }
  }, [loading, evaluationData]);

  // 绘制所有图表
  const drawCharts = useCallback(() => {
    drawStructureRadarChart();
    drawInterventionRadarChart();
    drawEfficiencyBarChart();
    drawSalaryBarChart();
  }, [evaluationData]);

  // 绘制结构雷达图，使用 sessionStorage 中的 tags_stage1 数据
  const drawStructureRadarChart = useCallback(() => {
    const ctx = document.getElementById('structure-radar-chart');
    if (!ctx) return;
    // 定义结构化标签顺序（须与后端保存时一致）
    const categories = ['標準化', 'ルール', 'マニュアル化', 'システム化', 'データ構造', '例外処理', '判断基準', '業務フロー'];
    const tagScoresStage1 = JSON.parse(sessionStorage.getItem("tags_stage1") || "{}");
    const scores = categories.map(cat => tagScoresStage1[cat] !== undefined ? tagScoresStage1[cat] : 0);
    chartRefs.current.structure = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: categories,
        datasets: [{
          label: '構造化レベル（タグスコア）',
          data: scores,
          backgroundColor: 'rgba(238, 67, 153, 0.2)',
          borderColor: 'rgba(238, 67, 153, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(238, 67, 153, 1)'
        }]
      },
      options: {
        scales: {
          r: { angleLines: { display: true }, suggestedMin: 0, suggestedMax: 100 }
        },
        plugins: { legend: { display: false } },
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }, []);

  // 绘制介入雷达图，使用 sessionStorage 中的 tags_stage2 数据
  const drawInterventionRadarChart = useCallback(() => {
    const ctx = document.getElementById('intervention-radar-chart');
    if (!ctx) return;
    const categories = ['判断', '確認', '例外対応', '調整', 'コミュニケーション', '専門知識', '創造性', '状況適応'];
    const tagScoresStage2 = JSON.parse(sessionStorage.getItem("tags_stage2") || "{}");
    const scores = categories.map(cat => tagScoresStage2[cat] !== undefined ? tagScoresStage2[cat] : 0);
    chartRefs.current.intervention = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: categories,
        datasets: [{
          label: '人間介入度（タグスコア）',
          data: scores,
          backgroundColor: 'rgba(39, 201, 24, 0.2)',
          borderColor: 'rgba(39, 201, 24, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(39, 201, 24, 1)'
        }]
      },
      options: {
        scales: {
          r: { angleLines: { display: true }, suggestedMin: 0, suggestedMax: 100 }
        },
        plugins: { legend: { display: false } },
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }, []);

  // 绘制作业时间削减效果柱状图
  const drawEfficiencyBarChart = useCallback(() => {
    const canvas = document.getElementById('efficiency-bar-chart');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
  
    const percentageRange = evaluationResult["時間節約割合"] || "0~0%";
    const { lower, upper } = parsePercentageRange(percentageRange);
  
    // 原始值（現状）
    const currentValue = monthlyWorkHours;
    // 保守シナリオ
    const worstCaseRemainingHours = monthlyWorkHours * (1 - lower / 100);
    // 最良シナリオ
    const bestCaseRemainingHours = monthlyWorkHours * (1 - upper / 100);
  
    // 计算差额
    const worstCaseDiff = currentValue - worstCaseRemainingHours;
    const bestCaseDiff = currentValue - bestCaseRemainingHours;
  
    chartRefs.current.efficiency = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: ['現状', '保守シナリオ', '最良シナリオ'],
        datasets: [{
          label: '作業時間（時間）',
          data: [
            currentValue,
            worstCaseRemainingHours,
            bestCaseRemainingHours
          ],
          backgroundColor: [
            'rgba(255, 159, 64, 0.6)',
            'rgba(41, 226, 24, 0.6)',
            'rgba(60, 95, 194, 0.6)'
          ],
          borderColor: [
            'rgba(255, 159, 64, 1)',
            'rgba(41, 226, 24, 1)',
            'rgba(60, 95, 194, 1)'
          ],
          borderWidth: 1,
          maxBarThickness: 60
        }]
      },
      options: {
        scales: {
          x: {},
          y: {
            beginAtZero: true,
            max: currentValue,
            title: {
              display: true,
              text: '作業時間（時間）'
            }
          }
        },
        plugins: {
          legend: { display: false },
          annotation: {
            annotations: {
              horizontalLine: {
                type: 'line',
                xScaleID: 'x',
                yScaleID: 'y',
                xMin: -0.4,
                xMax: 2.4,
                yMin: currentValue,
                yMax: currentValue,
                borderColor: '#666',
                borderWidth: 1,
                borderDash: [6, 3]
              },
              lineConservative: {
                type: 'line',
                xScaleID: 'x',
                yScaleID: 'y',
                xMin: 1,
                xMax: 1,
                yMin: currentValue,
                yMax: worstCaseRemainingHours,
                borderColor: '#666',
                borderWidth: 1,
                borderDash: [6, 3],
                arrowHeads: {
                  end: {
                    display: true,
                    fill: true,
                    length: 6,
                    width: 6
                  }
                },
                label: {
                  display: true,
                  content: `削減可能時間: ${worstCaseDiff.toFixed(1)}時間`,
                  position: 'center',
                  backgroundColor: '#fff',
                  color: '#333',
                  borderColor: '#666',
                  borderWidth: 1,
                  padding: 4,
                  font: {
                    size: 12,
                    weight: 'normal',
                    family: 'sans-serif'
                  }
                }
              },
              lineBest: {
                type: 'line',
                xScaleID: 'x',
                yScaleID: 'y',
                xMin: 2,
                xMax: 2,
                yMin: currentValue,
                yMax: bestCaseRemainingHours,
                borderColor: '#666',
                borderWidth: 1,
                borderDash: [6, 3],
                arrowHeads: {
                  end: {
                    display: true,
                    fill: true,
                    length: 6,
                    width: 6
                  }
                },
                label: {
                  display: true,
                  content: `削減可能時間: ${bestCaseDiff.toFixed(1)}時間`,
                  position: 'center',
                  backgroundColor: '#fff',
                  color: '#333',
                  borderColor: '#666',
                  borderWidth: 1,
                  padding: 4,
                  font: {
                    size: 12,
                    weight: 'normal',
                    family: 'sans-serif'
                  }
                }
              }
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }, [evaluationResult, monthlyWorkHours]);
  
  // 绘制成本削减效果柱状图
  const drawSalaryBarChart = useCallback(() => {
    const canvas = document.getElementById('salary-bar-chart');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
  
    const percentageRange = evaluationResult["時間節約割合"] || "0~0%";
    const { lower, upper } = parsePercentageRange(percentageRange);
    
    const currentValue = Math.floor(monthlySalary / 10000);
    const worstCaseRemainingCost = (monthlySalary * (1 - lower / 100) / 10000).toFixed(1);
    const bestCaseRemainingCost = (monthlySalary * (1 - upper / 100) / 10000).toFixed(1);
  
    const conservativeDiff = (currentValue - worstCaseRemainingCost).toFixed(1);
    const bestDiff = (currentValue - bestCaseRemainingCost).toFixed(1);
  
    chartRefs.current.salary = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: ['現状', '保守シナリ）', '最良シナリオ'],
        datasets: [{
          label: 'コスト（万円）',
          data: [currentValue, worstCaseRemainingCost, bestCaseRemainingCost],
          backgroundColor: [
            'rgba(212, 24, 24, 0.6)',
            'rgba(83, 64, 255, 0.6)',
            'rgba(255, 205, 86, 0.6)'
          ],
          borderColor: [
            'rgba(212, 24, 24, 1)',
            'rgba(83, 64, 255, 1)',
            'rgba(255, 205, 86, 1)'
          ],
          borderWidth: 1,
          maxBarThickness: 60
        }]
      },
      options: {
        scales: {
          x: {},
          y: {
            beginAtZero: true,
            max: currentValue,
            title: {
              display: true,
              text: 'コスト（万円）'
            }
          }
        },
        plugins: {
          legend: { display: false },
          annotation: {
            annotations: {
              horizontalLine: {
                type: 'line',
                xScaleID: 'x',
                yScaleID: 'y',
                xMin: -0.4,
                xMax: 2.4,
                yMin: currentValue,
                yMax: currentValue,
                borderColor: '#666',
                borderWidth: 1,
                borderDash: [6, 3]
              },
              lineConservative: {
                type: 'line',
                xScaleID: 'x',
                yScaleID: 'y',
                xMin: 1,
                xMax: 1,
                yMin: currentValue,
                yMax: worstCaseRemainingCost,
                borderColor: '#666',
                borderWidth: 1,
                borderDash: [6, 3],
                arrowHeads: {
                  end: {
                    display: true,
                    fill: true,
                    length: 6,
                    width: 6
                  }
                },
                label: {
                  display: true,
                  content: `削減可能額: ${conservativeDiff}万円`,
                  position: 'center',
                  backgroundColor: '#fff',
                  color: '#333',
                  borderColor: '#666',
                  borderWidth: 1,
                  padding: 4,
                  font: {
                    size: 12,
                    weight: 'normal',
                    family: 'sans-serif'
                  }
                }
              },
              lineBest: {
                type: 'line',
                xScaleID: 'x',
                yScaleID: 'y',
                xMin: 2,
                xMax: 2,
                yMin: currentValue,
                yMax: bestCaseRemainingCost,
                borderColor: '#666',
                borderWidth: 1,
                borderDash: [6, 3],
                arrowHeads: {
                  end: {
                    display: true,
                    fill: true,
                    length: 6,
                    width: 6
                  }
                },
                label: {
                  display: true,
                  content: `削減可能額: ${bestDiff}万円`,
                  position: 'center',
                  backgroundColor: '#fff',
                  color: '#333',
                  borderColor: '#666',
                  borderWidth: 1,
                  padding: 4,
                  font: {
                    size: 12,
                    weight: 'normal',
                    family: 'sans-serif'
                  }
                }
              }
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }, [evaluationResult, monthlySalary]);  
  
  // 生成唯一指纹（替代 unescape）
const generateDataFingerprint = () => {
  const fingerprint = {
    score1: Score1,
    score2: Score2,
    answeredQuestions,
  };
  const json = JSON.stringify(fingerprint);
  const utf8Bytes = new TextEncoder().encode(json);
  return btoa(String.fromCharCode(...utf8Bytes));
};


const handleReportSubmit = async () => {
  if (isGeneratingReport) return;
  try {
    setIsGeneratingReport(true);
    const base64 = await generatePdfBase64();

    const response = await fetch(`${API_BASE_URL}/report`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ pdf_base64: base64, email: email || "", name: name || "" }),
    });

    if (!response.ok) throw new Error(`Status: ${response.status}`);
  } catch (err) {
    console.error("PDF送信失敗:", err);
  } finally {
    setIsGeneratingReport(false);
  }
};

const sendBackupReportIfNeeded = async () => {
  const fingerprint = generateDataFingerprint();
  const alreadySent = localStorage.getItem("report_backup_sent");
  if (alreadySent === fingerprint) return;

  try {
    const base64 = await generatePdfBase64();
    console.log('PDF base64 length:', base64.length); 
    console.log('≈ size in MB:', (base64.length * 3 / 4 / 1024 / 1024).toFixed(2));

    await fetch(`${API_BASE_URL}/report`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ pdf_base64: base64, email: `backup_${email}`, name: name || "", fingerprint }),
    });

    localStorage.setItem("report_backup_sent", fingerprint);
  } catch (err) {
    console.error("バックアップ送信失敗:", err);
  }
};

const showPdfMask = () => {
  const mask = document.createElement('div');
  mask.id = 'pdf-mask';
  mask.style.position = 'fixed';
  mask.style.top = '0';
  mask.style.left = '0';
  mask.style.width = '100vw';
  mask.style.height = '100vh';
  mask.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
  mask.style.zIndex = '9999';
  mask.style.display = 'flex';
  mask.style.justifyContent = 'center';
  mask.style.alignItems = 'center';
  mask.style.fontSize = '1.5rem';
  mask.style.color = '#333';
  mask.style.fontFamily = "'Noto Sans JP', sans-serif";
  mask.innerText = 'Loading...';
  document.body.appendChild(mask);
};

const hidePdfMask = () => {
  const mask = document.getElementById('pdf-mask');
  if (mask) {
    document.body.removeChild(mask);
  }
};

const generatePdfBase64 = async () => {
  showPdfMask(); // 加遮罩

  try {
    const element = document.querySelector(".result-page");
    if (!element) throw new Error("レポート対象が見つかりません");

    const resultSummaryCard = element.querySelector(".result-summary-card");
    const buttonContainer = element.querySelector(".button-container");
    const resultSummary = element.querySelector(".result-summary");

    const originalStyle = {
      elementMarginTop: element.style.marginTop,
      elementPaddingTop: element.style.paddingTop,
      elementWidth: element.style.width,
      cardMarginTop: resultSummaryCard?.style.marginTop,
      buttonDisplay: buttonContainer?.style.display,
      resultGridTemplate: resultSummary?.style.gridTemplateColumns,
    };

    // 样式调整
    element.style.marginTop = "0px";
    element.style.paddingTop = "0px";
    element.style.width = "794px";
    if (resultSummaryCard) resultSummaryCard.style.marginTop = "0px";
    if (buttonContainer) buttonContainer.style.display = "none";
    if (resultSummary) resultSummary.style.gridTemplateColumns = "repeat(4, 1fr)";

    // 添加 footer
    const footer = document.createElement("div");
    footer.className = "pdf-footer";
    footer.style.marginTop = "60px";
    footer.style.padding = "20px 40px";
    footer.style.textAlign = "center";
    footer.style.fontSize = "1rem";
    footer.style.color = "#000";
    footer.style.lineHeight = "1.8";
    footer.style.fontFamily = "'Noto Sans JP', sans-serif";
    footer.style.borderTop = "1px solid #aaa";
    footer.innerHTML = `
      <p style="font-weight: bold; font-size: 1.1rem; margin-bottom: 6px;">
        株式会社 永栄（Nagae）　―　人間知能拡張のパートナー
      </p>
      <p>〒135-0063 東京都江東区有明3丁目5-7 TOC有明 ウエストタワー16階</p>
      <p>TEL: 03-6380-7500 ／ 携帯: 03-6380-7500</p>
      <p>E-mail: <a href="mailto:visa@nagae-jp.com" style="color: #000; text-decoration: none;">visa@nagae-jp.com</a></p>
    `;
    element.appendChild(footer);

    await new Promise((r) => setTimeout(r, 500));

    const opt = {
      margin: 0,
      image: { type: 'jpeg', quality: 0.9 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: 794,
        windowHeight: element.scrollHeight
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      },
      pagebreak: {
        mode: ['avoid-all']
      }
    };

    const uri = await html2pdf().from(element).set(opt).outputPdf('datauristring');

    // 样式恢复
    element.style.marginTop = originalStyle.elementMarginTop;
    element.style.paddingTop = originalStyle.elementPaddingTop;
    element.style.width = originalStyle.elementWidth;
    if (resultSummaryCard) resultSummaryCard.style.marginTop = originalStyle.cardMarginTop;
    if (buttonContainer) buttonContainer.style.display = originalStyle.buttonDisplay;
    if (resultSummary) resultSummary.style.gridTemplateColumns = originalStyle.resultGridTemplate;

    footer.remove();

    return uri.split(',')[1];
  } finally {
    hidePdfMask(); // 无论成功失败都关闭遮罩
  }
};


  const handleGoBack = () => navigate("/survey");

  if (loading) return (
    <div className="loading-container">
      <div className="spinner"></div>
      <p>データを取得中...</p>
    </div>
  );
  
  if (error) return (
    <div className="error-container">
      <div className="error-icon">!</div>
      <h2>エラーが発生しました</h2>
      <p>{error}</p>
      <button className="btn btn-primary" onClick={handleGoBack}>
        診断に戻る
      </button>
    </div>
  );
  
  if (!evaluationData || Object.keys(evaluationData).length === 0) return (
    <div className="empty-data-container">
      <p>データがありません。もう一度診断をお試しください。</p>
      <button className="btn btn-primary" onClick={handleGoBack}>
        診断に戻る
      </button>
    </div>
  );

  const getAutomationPossibility = (score) => {
    if (score >= 70) return "高";
    if (score >= 40) return "中";
    return "低";
  };
  const automationPossibility = getAutomationPossibility(numericScore);
  const timeSavingPercentage = evaluationResult["時間節約割合"] || "0%";
  const businessType = evaluationResult["業務分類"] || "不明";

  return (
    <div className="app-container">
      <Helmet>
        <link 
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@300;400;500;700&display=swap" 
          rel="stylesheet" 
        />
      </Helmet>
      <header className="header">
        <div className="logo">
          <h1></h1>
          <p className="subtitle"></p>
        </div>
      </header>
      <main className="main-content">
        <section id="results-screen" className="screen active result-page">
          <div className="card result-summary-card" style={{ marginTop: '-100px' }}>
            <div className="section-title">診断結果サマリー</div>
            <div className="business-name-row">
              業務名：{businessName}
            </div>
            <div className="result-summary">
              <div className="result-item">
                <div className="result-label">業務タイプ</div>
                <div id="business-type" className="result-value">
                  {businessType}
                </div>
              </div>
              <div className="result-item">
                <div className="result-label">構造化レベルスコア</div>
                <div id="structure-score" className="result-value">
                  {Score1.toFixed(1)}
                </div>
              </div>
              <div className="result-item">
                <div className="result-label">人間介入度スコア</div>
                <div id="intervention-score" className="result-value">
                  {Score2_display.toFixed(1)}
                </div>
              </div>
              <div className="result-item">
                <div className="result-label">自動化可能性</div>
                <div id="automation-possibility" className="result-value">
                  {automationPossibility}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 className="section-title">業務特性分析</h2>
            <div className="charts-container">
              <div className="chart-wrapper">
                <h3 className="chart-title">構造化レベル</h3>
                <div className="chart-container">
                  <canvas id="structure-radar-chart"></canvas>
                </div>
              </div>
              <div className="chart-wrapper">
                <h3 className="chart-title">人間介入度</h3>
                <div className="chart-container">
                  <canvas id="intervention-radar-chart"></canvas>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 className="section-title">パーソナライズされた改善提案</h2>
            {Object.entries(tagResult).map(([tagName, tagData]) => {
              const [tagMark, tagDescription] = tagData;
              return (
                <div key={tagName} className={`personalized-analysis-recommendation-${tagMark}`}>
                  <div className="personalized-analysis-recommendation-title">
                    <span className={`personalized-analysis-tag personalized-analysis-tag-${tagMark}`}>
                      {tagName}
                    </span>
                  </div>
                  <div className="personalized-analysis-recommendation-description">
                    {tagDescription}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card">
            <h2 className="section-title">ROI分析</h2>
            <div className="roi-container">
              <div className="roi-summary">
                <div className="roi-item">
                  <div className="roi-label">時間節約割合</div>
                  <div id="monthly-time-saved" className="roi-value">
                    {timeSavingPercentage}
                  </div>
                </div>
                <div className="roi-item">
                  <div className="roi-label">月間削減作業時間</div>
                  <div id="monthly-work-hours" className="roi-value">
                    {Math.floor(monthlyWorkHours * savingLower / 100)}~{Math.floor(monthlyWorkHours * savingUpper / 100)}時間
                  </div>
                </div>
                <div className="roi-item">
                  <div className="roi-label">月間削減コスト</div>
                  <div id="annual-cost-saving" className="roi-value">
                    {Math.floor(monthlySalary * savingLower / 1000000)}~{Math.floor(monthlySalary * savingUpper / 1000000)}万円
                  </div>
                </div>
              </div>
              <div className="charts-container">
                <div className="chart-wrapper">
                  <h3 className="chart-title">作業時間削減効果</h3>
                  <div className="chart-container">
                    <canvas id="efficiency-bar-chart"></canvas>
                  </div>
                </div>
                <div className="chart-wrapper">
                  <h3 className="chart-title">コスト削減効果</h3>
                  <div className="chart-container">
                    <canvas id="salary-bar-chart"></canvas>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 className="section-title">改善提案</h2>
            <div id="improvement-proposal" className="improvement-container">
              <div dangerouslySetInnerHTML={{ __html: evaluationResult["改善提案"].replace(/<\/br>/g, '<br>　') || "不明" }} />
            </div>
            <div className="button-container">
              <button 
                id="restart-button" 
                className="btn btn-secondary" 
                onClick={handleGoBack}
                disabled={isGeneratingReport}
              >
                もう一度診断する
              </button>
              <button 
                id="download-report-button" 
                className="btn btn-primary" 
                onClick={handleReportSubmit}
                disabled={isGeneratingReport}
              >
                {isGeneratingReport ? 'レポート送信中...' : 'レポートを送信する'}
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ResultPage;
